/**
 * Features grid — matches `documents/Web Version/website-standalone.html` (#features)
 * and marketing screenshot (“Built for Modern Teams”).
 */

const features: {
  title: string;
  desc: string;
  icon: 'bolt' | 'brain' | 'shield';
}[] = [
  {
    title: 'Real-time Bidding',
    desc: 'Experience lightning-fast reverse auctions with instant price updates and leaderboards.',
    icon: 'bolt',
  },
  {
    title: 'Bid Analysis',
    desc: 'Get intelligent insights and automated summaries for every procurement cycle.',
    icon: 'brain',
  },
  {
    title: 'Secure & Transparent',
    desc: 'Enterprise-grade security with full audit trails for every bid and transaction.',
    icon: 'shield',
  },
];

function FeatureIcon({ name }: { name: (typeof features)[number]['icon'] }) {
  const cls = 'h-8 w-8 text-blue-600 group-hover:text-white transition-colors';
  switch (name) {
    case 'bolt':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
        </svg>
      );
    case 'brain':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
        </svg>
      );
    case 'shield':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path strokeLinecap="round" d="M12 5v17" />
        </svg>
      );
    default:
      return null;
  }
}

export function LandingFeaturesSection() {
  return (
    <section
      id="features"
      className="scroll-mt-24 py-24 sm:py-32 bg-slate-50 px-4 sm:px-6"
    >
      <div className="max-w-7xl mx-auto">
        <header className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4">
            Built for Modern Teams
          </h2>
          <p className="text-gray-500 text-base sm:text-lg font-medium leading-relaxed">
            Everything you need to manage complex procurement cycles in one powerful platform.
          </p>
        </header>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((f) => (
            <div
              key={f.title}
              className="group bg-white p-8 sm:p-10 rounded-[40px] shadow-xl border border-gray-50 hover:scale-[1.02] transition-all duration-300 text-left"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-blue-600 transition-colors">
                <FeatureIcon name={f.icon} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-4">{f.title}</h3>
              <p className="text-gray-500 font-medium leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
